import { msg } from "@lingui/core/macro";
import { createSignal } from "solid-js";
import { createRoute } from "@tanstack/solid-router";
import { appRoute } from "./route";
import { Button } from "@/components/ui/button";
import { PageContainer } from "@fleet/ui/page-container";
import { Card, CardContent, CardHeader, CardTitle } from "@fleet/ui/card";
import { InviteMemberDialog } from "@/features/organizations/components/invite-member-dialog";
import { ChangeRoleDialog } from "@/features/organizations/components/change-role-dialog";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { showToast } from "@/components/ui/toast";
import { useI18n } from "@/providers/i18n-provider";
import { AlertTriangle, Hammer, Shield, UserPlus } from "@fleet/icons";

const SIMULATED_DELAY = 1000;

/**
 * Modal sandbox (DEV): an interactive environment to validate the UX and
 * transitions of the generic overlays. F0b+ adds the Agape domain dialogs
 * here as they exist.
 */
function ModalSandboxPage() {
  const { t } = useI18n();
  const [inviteOpen, setInviteOpen] = createSignal(false);
  const [roleOpen, setRoleOpen] = createSignal(false);

  return (
    <div class="flex flex-1 flex-col min-h-0 overflow-hidden bg-background">
      <PageContainer maxWidth="4xl" class="space-y-8" containerClass="min-h-0">
        {/* Header */}
        <div>
          <h1 class="text-3xl font-extrabold tracking-tight flex items-center gap-2">
            <Hammer class="h-8 w-8 text-primary" />
            {t(msg`Modal Sandbox`)}
          </h1>
          <p class="text-sm text-muted-foreground">
            {t(
              msg`Interactive testing environment for validating user experience and transitions of all application modals.`,
            )}
          </p>
        </div>

        {/* Modals Testing Cards Grid */}
        <div class="grid gap-6 sm:grid-cols-2">
          {/* Invite Member Dialog */}
          <Card class="hover:shadow-lg transition-shadow">
            <CardHeader class="flex flex-row items-center gap-4">
              <div class="h-10 w-10 rounded-xl bg-indigo-500/10 flex items-center justify-center text-indigo-500">
                <UserPlus class="h-5 w-5" />
              </div>
              <div>
                <CardTitle class="text-lg font-bold">{t(msg`Invite Member`)}</CardTitle>
                <p class="text-xs text-muted-foreground">{t(msg`Organization invitation form`)}</p>
              </div>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => setInviteOpen(true)}
                class="w-full bg-indigo-600 hover:bg-indigo-500"
              >
                {t(msg`Open Invite Dialog`)}
              </Button>
            </CardContent>
          </Card>

          {/* Change Role Dialog */}
          <Card class="hover:shadow-lg transition-shadow">
            <CardHeader class="flex flex-row items-center gap-4">
              <div class="h-10 w-10 rounded-xl bg-amber-500/10 flex items-center justify-center text-amber-500">
                <Shield class="h-5 w-5" />
              </div>
              <div>
                <CardTitle class="text-lg font-bold">{t(msg`Change Role`)}</CardTitle>
                <p class="text-xs text-muted-foreground">
                  {t(msg`Permission selector for members`)}
                </p>
              </div>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => setRoleOpen(true)}
                class="w-full bg-amber-600 hover:bg-amber-500"
              >
                {t(msg`Open Role Dialog`)}
              </Button>
            </CardContent>
          </Card>

          {/* Confirm Dialog */}
          <Card class="hover:shadow-lg transition-shadow col-span-1 sm:col-span-2">
            <CardHeader class="flex flex-row items-center gap-4">
              <div class="h-10 w-10 rounded-xl bg-destructive/10 flex items-center justify-center text-destructive">
                <AlertTriangle class="h-5 w-5" />
              </div>
              <div>
                <CardTitle class="text-lg font-bold">{t(msg`Confirm Dialog`)}</CardTitle>
                <p class="text-xs text-muted-foreground">
                  {t(msg`Confirmation window for destructive actions`)}
                </p>
              </div>
            </CardHeader>
            <CardContent>
              <ConfirmDialog
                title={t(msg`Do you want to save destructive changes?`)}
                description={t(
                  msg`This is an interactive demonstration of ConfirmDialog in the Modal Sandbox testing environment.`,
                )}
                confirmLabel={t(msg`Confirm action`)}
                variant="destructive"
                onConfirm={() => {
                  showToast(t(msg`Action confirmed in Sandbox`), "success");
                }}
                renderTrigger={(open) => (
                  <Button onClick={open} variant="destructive" class="w-full">
                    {t(msg`Open Confirm Dialog`)}
                  </Button>
                )}
              />
            </CardContent>
          </Card>
        </div>

        {/* Overlay instances */}
        <InviteMemberDialog
          open={inviteOpen()}
          onOpenChange={setInviteOpen}
          // oxlint-disable-next-line typescript-eslint/promise-function-async
          onInvite={(email) => {
            console.log("Inviting:", email);
            return new Promise((resolve) => {
              setTimeout(resolve, SIMULATED_DELAY);
            });
          }}
        />

        <ChangeRoleDialog
          open={roleOpen()}
          onOpenChange={setRoleOpen}
          currentRole="member"
          memberName={t(msg`Juan Pérez`)}
          // oxlint-disable-next-line typescript-eslint/promise-function-async
          onRoleChange={(role) => {
            console.log("Role changed to:", role);
            return Promise.resolve();
          }}
        />
      </PageContainer>
    </div>
  );
}

export const sandboxModalRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/modal",
  component: ModalSandboxPage,
});
